




















































































































































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import router from "@/router/router";
import { GroupUserRow, SharedEmailAddressRow, SharedEmailUserRow, SharedUsersList } from "@/data/models/SharedUsers";
import {
  cachedSharedUsers,
  cleanUpSharedEmailAddress,
  listSharedUsersForAdministration,
} from "@/data/repository/SharedUsersRepository";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { readThroughCache } from "@/utils/AdminReadCache";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { userStatus } from "@/utils/ClientUserList";
import { COLOR_ALL_DEVICES_EDIT_ICON, COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { ComponentName } from "@/utils/RouteGuards";
import {
  selectGroupUsers,
  selectSharedEmailAddresses,
  SHARED_EMAIL_ADDRESS_FILTERS,
  SharedEmailAddressFilter,
  usersToCleanUp,
} from "@/utils/SharedUserList";
import { themed } from "@/utils/Theme";

type SharedUsersTab = "emailAddresses" | "group";
const TABS: SharedUsersTab[] = ["emailAddresses", "group"];

/**
 * Geteilte Benutzer in the IRES Admin Client: both kinds of shared users.
 *
 * - **E-Mail-Adresse teilen:** every entry with its users' states. An entry whose users are gone blocks
 *   its address for any new user, and one whose Heimat-Mandant user is deleted locks IRES Office's
 *   sign-in out, so Bereinigen takes those users out (and deletes an entry nobody is left in). The only
 *   change offered (Taimoor, 2026-11-03).
 * - **Gruppe:** the shared users of each group, view only; changes stay in the master's user form.
 *
 * Opened from the portal page, or from a chip in Benutzer suchen with `tab` and `search` (the address).
 * **Kept alive**: back from a group's client shows the list at once and refreshes it quietly.
 *
 * **Not the access boundary**: IRES Cloud repeats the portal and `MANAGE_CLIENTS` checks, refuses
 * Bereinigen for the shared IRES admin login, and never sends password data.
 */
@Component({
  name: "AdminSharedUsers",
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "content-state": ContentStateComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class AdminSharedUsers extends Vue {
  @Ref("confirmationDialog") readonly confirmationDialog!: ConfirmationDialogComponent;

  list: SharedUsersList = { emailAddresses: [], groupUsers: [] };
  tabIndex = 0;
  search = "";
  filter: SharedEmailAddressFilter = "ALL";
  expanded: string[] = [];
  isLoading = true;
  /** Showing the cached list while IRES Cloud is asked again; changes wait for its answer. */
  isRefreshing = false;
  isCleaningUp = false;
  /** Alle bereinigen's progress while it runs, else null. */
  cleanUpProgress: { done: number; total: number } | null = null;
  loadError = false;
  /** Gates the app-bar portal: a cached instance must not fill the bar of another screen. */
  componentActive = false;

  readonly filters = SHARED_EMAIL_ADDRESS_FILTERS;
  readonly emptyValue = EMPTY_VALUE;
  readonly editIconColor = COLOR_ALL_DEVICES_EDIT_ICON;

  get tab(): SharedUsersTab {
    return TABS[this.tabIndex] ?? "emailAddresses";
  }

  get emailAddresses(): SharedEmailAddressRow[] {
    return selectSharedEmailAddresses(this.list.emailAddresses, this.filter, this.search);
  }

  get groupUsers(): GroupUserRow[] {
    return selectGroupUsers(this.list.groupUsers, this.search);
  }

  get shownCount(): number {
    return this.tab === "emailAddresses" ? this.emailAddresses.length : this.groupUsers.length;
  }

  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.shownCount > 0;
  }

  /** Every entry Bereinigen would change, whatever the filter shows. */
  get toCleanUp(): SharedEmailAddressRow[] {
    return this.list.emailAddresses.filter((entry) => entry.state !== "OK");
  }

  /** Phones (< 600 px, ui-conventions.md). */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The grey line under a phone's title line: the filter when it is not Alle. */
  get phoneHeaderDetails(): string {
    return this.tab === "emailAddresses" && this.filter !== "ALL" ? this.filterName(this.filter) : "";
  }

  /** "Alle bereinigen (39)", counting "Bereinige 12 von 39 …" while it runs. */
  get cleanUpAllLabel(): string {
    return this.cleanUpProgress
      ? this.$t("admin.sharedUsers.cleaningUp", [this.cleanUpProgress.done, this.cleanUpProgress.total]).toString()
      : this.$t("admin.sharedUsers.cleanUpAll", [this.toCleanUp.length]).toString();
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** A client's current name, by the names IRES Cloud sent with the rows. */
  get clientNames(): Record<string, string> {
    const names: Record<string, string> = {};
    for (const entry of this.list.emailAddresses) {
      for (const user of entry.users) {
        if (user.clientName) {
          names[user.clientId] = user.clientName;
        }
      }
    }
    for (const user of this.list.groupUsers) {
      if (user.administratorClientName) {
        names[user.administratorClientId] = user.administratorClientName;
      }
    }
    return names;
  }

  /** A client's name as far as the rows know it; never its id, which tells the user nothing. */
  clientName(clientId: string | null): string {
    return (clientId && this.clientNames[clientId]) || EMPTY_VALUE;
  }

  homeClientName(entry: SharedEmailAddressRow): string {
    return entry.sourceClientName || this.clientName(entry.sourceClientId || null);
  }

  filterName(filter: SharedEmailAddressFilter): string {
    return this.$t(`admin.sharedUsers.filters.${filter}`).toString();
  }

  stateLabel(entry: SharedEmailAddressRow): string {
    if (entry.state === "STALE") {
      return this.$t("admin.sharedUsers.states.STALE", [usersToCleanUp(entry).length]).toString();
    }
    return this.$t(`admin.sharedUsers.states.${entry.state}`).toString();
  }

  stateColor(entry: SharedEmailAddressRow): string {
    return entry.state === "OK" ? "success" : entry.state === "STALE" ? "warning" : "error";
  }

  userName(user: SharedEmailUserRow): string {
    if (user.name && user.nickname) {
      return `${user.name} (${user.nickname})`;
    }
    return user.name ?? user.nickname ?? EMPTY_VALUE;
  }

  groupUserName(user: GroupUserRow): string {
    const name = [user.firstName, user.lastName].filter((part) => !!part).join(" ");
    if (name && user.nickname) {
      return `${name} (${user.nickname})`;
    }
    return name || user.nickname || EMPTY_VALUE;
  }

  groupStatusLabel(user: GroupUserRow): string {
    return this.$t(`admin.clientUsers.statuses.${userStatus(user)}`).toString();
  }

  /** Hover lightens the row in dark mode, where the shadow is invisible. */
  rowStyle(hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    return background ? `background-color: ${background}` : "";
  }

  isExpanded(id: string): boolean {
    return this.expanded.includes(id);
  }

  toggle(id: string) {
    this.expanded = this.isExpanded(id) ? this.expanded.filter((entry) => entry !== id) : [...this.expanded, id];
  }

  /** The group's master, where its shared users are managed. */
  openGroup(user: GroupUserRow) {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: user.administratorClientId } });
  }

  /** The confirmation names who goes and whether the entry goes too. */
  async onCleanUp(entry: SharedEmailAddressRow) {
    const gone = usersToCleanUp(entry);
    const confirmed = await this.confirmationDialog.show({
      title: this.$t("admin.sharedUsers.confirm.title").toString(),
      message: [
        this.$t("admin.sharedUsers.confirm.message", [entry.emailAddress]).toString(),
        ...gone.map((user) => `• ${this.userName(user)}, ${user.clientName ?? EMPTY_VALUE}`),
        gone.length === entry.users.length
          ? this.$t("admin.sharedUsers.confirm.entryDeleted").toString()
          : this.$t("admin.sharedUsers.confirm.othersStay").toString(),
      ].join("\n"),
      acceptMessage: this.$t("admin.sharedUsers.cleanUp").toString(),
    });
    if (!confirmed) {
      return;
    }

    this.isCleaningUp = true;
    try {
      await cleanUpSharedEmailAddress(entry.id);
      SnackbarModule.showSuccess({ key: "admin.sharedUsers.cleanedUp", params: [entry.emailAddress] });
    } catch (error) {
      SnackbarModule.showError({ key: this.refusalKey(error) });
    } finally {
      this.isCleaningUp = false;
    }
    await this.reloadInBackground();
  }

  /** Every entry to clean up, one after another; stops at the first refusal. */
  async onCleanUpAll() {
    const entries = [...this.toCleanUp];
    const confirmed = await this.confirmationDialog.show({
      title: this.$t("admin.sharedUsers.confirm.title").toString(),
      message: this.$t("admin.sharedUsers.confirm.allMessage", [entries.length]).toString(),
      acceptMessage: this.$t("admin.sharedUsers.cleanUp").toString(),
    });
    if (!confirmed) {
      return;
    }

    this.isCleaningUp = true;
    let done = 0;
    this.cleanUpProgress = { done, total: entries.length };
    try {
      for (const entry of entries) {
        await cleanUpSharedEmailAddress(entry.id);
        done += 1;
        this.cleanUpProgress = { done, total: entries.length };
      }
      SnackbarModule.showSuccess({ key: "admin.sharedUsers.cleanedUpAll", params: [String(done)] });
    } catch (error) {
      SnackbarModule.showError({ key: this.refusalKey(error) });
    } finally {
      this.isCleaningUp = false;
      this.cleanUpProgress = null;
    }
    await this.reloadInBackground();
  }

  refusalKey(error: unknown): string {
    const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
    return reason === "SHARED_LOGIN" || reason === "NO_ENTRY"
      ? `admin.sharedUsers.refusals.${reason}`
      : "admin.sharedUsers.cleanUpFailed";
  }

  /** The cached list at once when it was read before in this tab, then IRES Cloud's. */
  async load() {
    const cached = cachedSharedUsers();
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.loadError = false;
    try {
      await readThroughCache(cached, listSharedUsersForAdministration, (list) => {
        this.list = list;
      });
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
      this.isRefreshing = false;
    }
  }

  /** Reads the list again without the loading state, so the rows on screen stay while it runs. */
  async reloadInBackground() {
    this.isRefreshing = true;
    try {
      this.list = await listSharedUsersForAdministration();
      this.loadError = false;
    } catch {
      // Swallowed on purpose: the list keeps what it showed, and the refresh button reloads on demand.
    } finally {
      this.isRefreshing = false;
    }
  }

  /** A link from Benutzer suchen names the tab and the address to show. Read once: each URL is its own instance. */
  applyRouteQuery() {
    const tab = TABS.indexOf(this.$route.query.tab as SharedUsersTab);
    if (tab >= 0) {
      this.tabIndex = tab;
    }
    if (typeof this.$route.query.search === "string") {
      this.search = this.$route.query.search;
      this.filter = "ALL";
    }
  }

  mounted() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.sharedUsers.title");
    this.applyRouteQuery();
    this.load();
  }

  /** Back from a group's client: rows at once, then a quiet refresh. */
  activated() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.sharedUsers.title");
    if (!this.isLoading && !this.isRefreshing) {
      this.reloadInBackground();
    }
  }

  deactivated() {
    this.componentActive = false;
  }
}
